/**
 * Farm View Map Component
 * Displays all fields colored by water availability level with NDVI layer toggle
 */

const React = require('react');
const { useState, useEffect, useRef, useCallback } = React;
const PropTypes = require('prop-types');
const { useTranslation } = require('react-i18next');
const CustomMap = require('CustomMap');
const farmingUnitManager = require('farmingUnitManager');
require('./FarmViewMap.css');

/**
 * Gets color for field based on water availability level
 * @param {string} waterAvailabilityLevel - 'none' | 'low' | 'medium' | 'high'
 * @returns {string} Color value
 */
const getWaterAvailabilityColor = (waterAvailabilityLevel) => {
  switch (waterAvailabilityLevel?.toLowerCase()) {
    case 'low':
      return '#DF3044'; // Critical - Red (needs water)
    case 'medium':
      return '#FFB302'; // Warning - Orange
    case 'high':
      return '#078378'; // Good - Green (sufficient water)
    case 'none':
    default:
      return '#DCE0E8'; // No data - Grey
  }
};

/**
 * FarmViewMap component
 * @param {Object} props
 * @param {Object} props.selectedFarmingUnit - The selected farming unit with fields
 * @param {Array} props.fields - Array of field data with water availability levels
 * @param {Function} props.onFieldClick - Callback when field is clicked
 * @param {string} props.selectedFieldId - Currently selected field ID
 * @param {boolean} props.showNDVI - Whether to show NDVI layer
 * @param {Function} props.onNDVIToggle - Callback for NDVI toggle
 * @param {boolean} props.archiveMode - Whether archive mode is active
 */
const FarmViewMap = ({
  selectedFarmingUnit,
  fields,
  onFieldClick,
  selectedFieldId,
  showNDVI,
  onNDVIToggle,
  archiveMode,
}) => {
  const { t } = useTranslation();
  const [customMapInstance, setCustomMapInstance] = useState(null);

  /**
   * Prepare and update map with water availability colors
   */
  const updateMap = useCallback(() => {
    // `fields` being undefined means the farming unit is still a skeleton (its
    // full distribution tree hasn't loaded yet) — its fields/sensors aren't
    // there, so the area/marker builders would dereference undefined. Bail
    // until the full tree lands; the effect re-runs when selectedFarmingUnit
    // gains its fields.
    if (
      !customMapInstance ||
      !selectedFarmingUnit ||
      !selectedFarmingUnit.fields
    ) {
      return;
    }

    const fieldsState = archiveMode ? 'archive' : 'active';

    // Get ALL fields from the farming unit using farmingUnitManager
    let all_areas = farmingUnitManager.all_farming_unit_areas(
      selectedFarmingUnit,
      '', // Empty string to show all fields
      fieldsState,
    );

    // Convert to array if object
    const areasArray = Array.isArray(all_areas)
      ? all_areas
      : Object.values(all_areas || {});

    // Apply colors based on water availability level
    const coloredAreas = areasArray.map((area) => {
      // Find the matching field data with water availability level
      const fieldData = fields.find((f) => f.id === area.id);
      const waterAvailabilityLevel =
        fieldData?.waterAvailabilityLevel || 'none';
      const color = getWaterAvailabilityColor(waterAvailabilityLevel);

      return {
        ...area,
        fill_color: color,
        strokeColor: color,
        strokeWeight: 3,
        strokeOpacity: 1.0,
        fillColor: color,
        fillOpacity: 0.3,
      };
    });

    // Convert back to object format expected by CustomMap
    const finalAreas = coloredAreas.reduce((obj, area) => {
      obj[area.id] = area;
      return obj;
    }, {});

    // Get markers if applicable
    let all_markers = {};
    if (
      selectedFarmingUnit &&
      farmingUnitManager.shouldDisplayAllSensors(selectedFarmingUnit)
    ) {
      all_markers = farmingUnitManager.all_farming_unit_markers(
        selectedFarmingUnit,
        '',
        fieldsState,
      );
    }

    // Invalidate and refresh the map with the new data
    customMapInstance.invalidate(finalAreas, all_markers, [{}], false, false);
  }, [customMapInstance, selectedFarmingUnit, fields, archiveMode]);

  /**
   * Update map when custom map instance is ready or data changes
   */
  useEffect(() => {
    if (
      customMapInstance &&
      selectedFarmingUnit &&
      selectedFarmingUnit.fields
    ) {
      updateMap();
    }
  }, [customMapInstance, selectedFarmingUnit, fields, archiveMode, updateMap]);

  /**
   * Handle field polygon click
   */
  const handlePolygonClick = (fieldId) => {
    if (onFieldClick) {
      onFieldClick(fieldId);
    }
  };

  /**
   * Handle marker click
   */
  const handleMarkerClick = () => {};

  /**
   * Handle NDVI toggle
   */
  const handleNDVIToggle = () => {
    if (onNDVIToggle) {
      onNDVIToggle();
    }
  };

  /**
   * Handle Escape key to deselect field
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedFieldId && onFieldClick) {
        onFieldClick(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFieldId, onFieldClick]);

  return (
    <div className='farm-view-map'>
      {/* Map Controls */}
      <div className='farm-view-map__controls'>
        <div className='farm-view-map__legend'>
          <span
            className='text-style-short-lables-12-semi-bold'
            style={{ color: 'var(--color-grey-45)', whiteSpace: 'pre-line' }}
          >
            {t('crop_water_availability')}
          </span>
          <div className='farm-view-map__legend-items'>
            <div className='farm-view-map__legend-item'>
              <div
                className='farm-view-map__legend-color'
                style={{ backgroundColor: '#078378' }}
              />
              <span
                className='text-style-short-lables-12-regular'
                style={{ color: 'var(--color-grey-45)' }}
              >
                {t('water_availability_high')}
              </span>
            </div>
            <div className='farm-view-map__legend-item'>
              <div
                className='farm-view-map__legend-color'
                style={{ backgroundColor: '#FFB302' }}
              />
              <span
                className='text-style-short-lables-12-regular'
                style={{ color: 'var(--color-grey-45)' }}
              >
                {t('water_availability_medium')}
              </span>
            </div>
            <div className='farm-view-map__legend-item'>
              <div
                className='farm-view-map__legend-color'
                style={{ backgroundColor: '#DF3044' }}
              />
              <span
                className='text-style-short-lables-12-regular'
                style={{ color: 'var(--color-grey-45)' }}
              >
                {t('water_availability_low')}
              </span>
            </div>
            <div className='farm-view-map__legend-item'>
              <div
                className='farm-view-map__legend-color'
                style={{ backgroundColor: '#DCE0E8' }}
              />
              <span
                className='text-style-short-lables-12-regular'
                style={{ color: 'var(--color-grey-45)' }}
              >
                {t('water_availability_no_data')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className='farm-view-map__container'>
        {/* NDVI Toggle - positioned on map, centered */}
        <label className='farm-view-map__ndvi-toggle'>
          <input
            type='checkbox'
            checked={showNDVI}
            onChange={handleNDVIToggle}
          />
          <span className='farm-view-map__ndvi-slider' />
          <span
            className='text-style-short-lables-14-regular'
            style={{ color: 'var(--color-grey-45)' }}
          >
            {t('ndvi_layer')}
          </span>
        </label>
        <CustomMap
          onRef={(ref) => setCustomMapInstance(ref)}
          mapId='irrigation-monitoring-farm-view-map'
          showEditToolBar={false}
          message=''
          onPolygonClick={handlePolygonClick}
          onMarkerClick={handleMarkerClick}
          onLayerSelection={() => {}}
          onNDVILayerSelection={() => {}}
          map_height={400}
          shouldPresentStressMenu={false}
          shouldPresentFilePicker={false}
          shouldDisplayNDVI={showNDVI}
        />
      </div>

      {/* Help text */}
      {selectedFieldId && (
        <div className='farm-view-map__help'>
          <span
            className='text-style-short-lables-12-regular'
            style={{ color: 'var(--color-grey-71)' }}
          >
            Press <kbd>Esc</kbd> to deselect field
          </span>
        </div>
      )}
    </div>
  );
};

FarmViewMap.propTypes = {
  selectedFarmingUnit: PropTypes.object,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      waterAvailabilityLevel: PropTypes.oneOf([
        'none',
        'low',
        'medium',
        'high',
      ]),
    }),
  ).isRequired,
  onFieldClick: PropTypes.func,
  selectedFieldId: PropTypes.string,
  showNDVI: PropTypes.bool,
  onNDVIToggle: PropTypes.func,
  archiveMode: PropTypes.bool,
};

FarmViewMap.defaultProps = {
  selectedFarmingUnit: null,
  onFieldClick: null,
  selectedFieldId: null,
  showNDVI: false,
  onNDVIToggle: null,
  archiveMode: false,
};

module.exports = FarmViewMap;
